<template>
  <div class="file-count">
    <div class="flex items-center justify-end gap-x-1 select-none" :class="attrsClass">
      <slot name="prepend" />
      <UPopover
        v-if="!hideFileCount && (displayCount > 0 || isUploading)"
        v-model:open="isPreviewMode"
        mode="hover"
        :open-delay="500"
        :close-delay="500"
        :dismissible="!isContextMenuOpen && !isDraggingPreview"
        :content="{ side: 'bottom', align: previewAlign, sideOffset: 6, collisionPadding: 8 }"
        :ui="{
          content: [
            'file-count-preview z-50 max-h-(--reka-hover-card-content-available-height) max-w-[calc(100vw-1rem)] bg-transparent shadow-none ring-0',
            isPreviewSliding
              ? 'pointer-events-none overflow-hidden [[data-reka-popper-content-wrapper]:has(>&)]:pointer-events-none'
              : 'overflow-y-auto',
            previewWidthClass
          ]
        }"
      >
        <PButton
          class="min-w-5 justify-center"
          :color="isActive ? 'primary-lighten-1' : 'primary'"
          :size="size"
          :variant="variant"
          :disabled="fileCollection.length === 0"
          :loading="isLoading || !windowStore.allWindowsLoaded"
          :icon="$slots.prepend ? undefined : ['fas', 'paperclip']"
          @pointerenter="suppressPreview = false"
          @focus="suppressPreview = false"
          @click.stop="onFileCountClick"
          title="Bekijk de bijlage, klik om direct te openen"
          tabindex="-1"
        >
          <div v-if="windowStore.allWindowsLoaded">
            <slot />
            {{ displayCount }}
          </div>
        </PButton>
        <template #content>
          <FilePreviewList
            v-model="fileCollection"
            inline
            :container-class="previewClass"
            :is-uploading="isUploading"
            :read-only="readOnly"
            :drag-default-is-copy="dragDefaultIsCopy"
            :on-delete="onDelete"
            :on-move="onMove"
            @close="disablePreviewMode"
            @open="onContextMenuOpenChange"
            @dragstart="onPreviewDragStart"
            @drag="isPreviewSliding = true"
            @dragend="disablePreviewMode"
          />
        </template>
      </UPopover>
      <PProgress
        v-if="!hideFileCount && isUploading"
        size="sm"
        class="w-12! shrink-0"
        aria-label="Bestand wordt geüpload"
      />
      <slot name="append" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, useAttrs, type HTMLAttributes } from "vue"
import { storeToRefs } from "pinia"
import UPopover from "@nuxt/ui/components/Popover.vue"
import FilePreviewList from "@/components/file/FilePreviewList.vue"
import PProgress from "@/components/elements/PProgress.vue"
import PButton from "@/components/elements/PButton.vue"
import { FileViewerController } from "@/controllers/file-viewer-controller"
import { FileRelation } from "@/models/file/file-relation.model"
import { useWindowStore } from "@/store/windows.store"
import { useLayoutColumnsStore } from "@/store/layout-columns.store"

defineOptions({
  inheritAttrs: false
})

export type ShowFileViewerPropOptions = "never" | "always" | number

const fileCollection = defineModel<FileRelation[]>({ required: true })
const attrs = useAttrs()
const attrsClass = computed(() => attrs.class as HTMLAttributes["class"])

const props = withDefaults(
  defineProps<{
    readOnly?: boolean
    showFileViewer?: ShowFileViewerPropOptions
    isUploading?: boolean
    size?: "sm" | "md" | "lg"
    variant?: InstanceType<typeof PButton>["$props"]["variant"]
    /**
     * Whether to hide the file count. This file count is clickable and will open the file viewer.
     */
    hideFileCount?: boolean
    dragDefaultIsCopy?: boolean
    /**
     * CSS class for the file preview content.
     */
    previewClass?: string
    /** Align the preview with the attachment button. */
    previewAlign?: "start" | "center" | "end"
    onDelete?: (fileRelation: FileRelation) => void
    onMove?: (fileRelation: FileRelation) => void
  }>(),
  {
    showFileViewer: "never",
    isUploading: false,
    hideFileCount: false,
    size: "sm",
    variant: undefined,
    previewClass: "",
    previewAlign: "end"
  }
)

const displayCount = computed(() => fileCollection.value.length)

const windowStore = useWindowStore()

const layoutColumnsStore = useLayoutColumnsStore()

const { fileViewers } = storeToRefs(layoutColumnsStore)

const { windows } = storeToRefs(windowStore)

const isActive = ref(false)
const isLoading = ref(false)
const hoverOpen = ref(false)
const suppressPreview = ref(false)
const isContextMenuOpen = ref(false)
const isDraggingPreview = ref(false)
const isPreviewSliding = ref(false)
const canPreview = computed(
  () =>
    fileCollection.value.length > 0 &&
    !props.hideFileCount &&
    !isLoading.value &&
    !suppressPreview.value
)
const isPreviewMode = computed({
  get: () =>
    canPreview.value && (hoverOpen.value || isContextMenuOpen.value || isDraggingPreview.value),
  set: (open: boolean) => {
    hoverOpen.value = open && canPreview.value
  }
})
const previewWidthClass = computed(() => {
  const count = fileCollection.value.length + (props.isUploading ? 1 : 0)
  return count <= 1 ? "w-48" : count === 2 ? "w-96" : "w-[36rem]"
})

// A hidden/empty preview must not reopen with stale hover or interaction state.
watch(canPreview, (allowed) => {
  if (!allowed) disablePreviewMode()
})

// Check if files is open in a Column or Window
watch(
  [fileViewers, () => windows.value],
  () => {
    isActive.value =
      fileViewers.value.some(
        (fileViewer) => fileViewer.props?.modelValue === fileCollection.value
      ) || windows.value.some((window) => window.props?.modelValue === fileCollection.value)
  },
  { deep: true }
)

function disablePreviewMode() {
  hoverOpen.value = false
  isContextMenuOpen.value = false
  isDraggingPreview.value = false
  isPreviewSliding.value = false
}

function onContextMenuOpenChange(open: boolean) {
  isContextMenuOpen.value = open
  // Once the pointer enters the portal menu, keep it open through the menu state.
  // Re-entering the preview lets UPopover request hover again.
  if (open) hoverOpen.value = false
}

function onPreviewDragStart(event: DragEvent) {
  if (props.readOnly || event.defaultPrevented) {
    return
  }
  // Keep the source mounted until native drag startup has captured the drag image.
  isDraggingPreview.value = true
  hoverOpen.value = false
}

async function onFileCountClick() {
  suppressPreview.value = true
  disablePreviewMode()
  isLoading.value = true

  if (fileCollection.value.length > 0) {
    try {
      await FileViewerController.display(fileCollection.value, fileCollection.value[0], {
        getArray: () => fileCollection.value,
        onDelete: props.onDelete,
        readOnly: props.readOnly
      })
    } finally {
      isLoading.value = false
    }
  }
}
</script>
